import React, { useEffect, useState } from "react";
import WeeklyAvailability from "./UserAvailability/WeeklyAvailability";
import UnavailableDates from "./UserAvailability/UnavailableDates";
import SpecialAvailabilities from "./UserAvailability/SpecialAvailabilities";
// Removed: import { useAuth } from "../../context/AuthContext";
import "../../styles/components/management/UserAvailability.css";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function UserAvailability() {
  const [allUsers, setAllUsers] = useState([]);
  const [weeklySettings, setWeeklySettings] = useState({});
  const [loadingWeekly, setLoadingWeekly] = useState(false);

  const [selectedUAUser, setSelectedUAUser] = useState("");
  const [uaDate, setUADate] = useState("");
  const [allUnavailable, setAllUnavailable] = useState([]);
  const [loadingUnavailable, setLoadingUnavailable] = useState(false);

  const [selectedSAUser, setSelectedSAUser] = useState("");
  const [saDate, setSADate] = useState("");
  const [allSpecials, setAllSpecials] = useState([]);
  const [loadingSpecial, setLoadingSpecial] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await fetch(`${API_BASE}/app/API/rbmgt/get_users.php`);
        const data = await res.json();
        const filtered = (data.users || []).filter((u) =>
          ["Supervisor", "Housekeeper"].includes(u.ROLE_NAME)
        );
        filtered.sort((a, b) => a.USER_SURNAME.localeCompare(b.USER_SURNAME));
        setAllUsers(filtered);

        const init = {};
        filtered.forEach((u) => {
          init[u.USER_ID] = {
            days: { 0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
            priority: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 },
            maxConsec: 6,
          };
        });
        setWeeklySettings(init);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    if (allUsers.length === 0) return;

    setLoadingWeekly(true);
    fetch(`${API_BASE}/app/API/rbmgt/get_user_availabilities.php`)
      .then(res => res.json())
      .then(data => {
        const ws = {};

        allUsers.forEach((u) => {
          ws[u.USER_ID] = {
            days: { 0: false, 1: false, 2: false, 3: false, 4: false, 5: false, 6: false },
            priority: { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 },
            maxConsec: 6,
          };
        });

        (data.avail_days || []).forEach((rec) => {
          const uid = rec.user_id;
          const wd = (Number(rec.weekday) + 6) % 7;
          if (!ws[uid]) return;
          ws[uid].days[wd] = rec.is_available === "1";
          ws[uid].priority[wd] = Number(rec.priority) || 0;
        });

        (data.max_consec || []).forEach((rec) => {
          const uid = rec.user_id;
          if (!ws[uid]) return;
          ws[uid].maxConsec = Number(rec.max_consecutive_days) || 6;
        });

        setWeeklySettings(ws);
      })
      .catch(err => {
        console.error("Failed to fetch weekly availability:", err);
      })
      .finally(() => {
        setLoadingWeekly(false);
      });
  }, [allUsers]);

  const handleSaveWeekly = async () => {
    setLoadingWeekly(true);
    try {
      const payload = {
        avail_days: [],
        max_consec: [],
      };
      Object.entries(weeklySettings).forEach(([uid, cfg]) => {
        Object.entries(cfg.days).forEach(([wd, enabled]) => {
          payload.avail_days.push({
            user_id: uid,
            weekday: (parseInt(wd) + 1) % 7,
            is_enabled: enabled ? 1 : 0,
            priority: cfg.priority[wd] || 0,
          });
        });
        payload.max_consec.push({
          user_id: uid,
          max_consecutive_days: cfg.maxConsec,
        });
      });

      const res = await fetch(`${API_BASE}/app/API/rbmgt/set_user_availabilities.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        console.error("Save weekly failed:", data.message);
      } else {
        alert("Weekly availability saved!");
      }
    } catch (err) {
      console.error("Error saving weekly availability:", err);
    } finally {
      setLoadingWeekly(false);
    }
  };

  const handleStarClick = (userId, wd) => {
    setWeeklySettings((prev) => {
      const copy = { ...prev };
      const cfg = { ...copy[userId] };
      const old = cfg.priority[wd] || 0;
      const next = old >= 5 ? 0 : old + 1;
      cfg.priority = { ...cfg.priority, [wd]: next };
      copy[userId] = cfg;
      return copy;
    });
  };

  useEffect(() => {
    const fetchUnavailable = async () => {
      setLoadingUnavailable(true);
      try {
        const res = await fetch(`${API_BASE}/app/API/rbmgt/get_unavailable_dates.php`);
        const data = await res.json();
        setAllUnavailable(data.unavail_dates || []);
      } catch (err) {
        console.error("Failed to fetch unavailable dates:", err);
      } finally {
        setLoadingUnavailable(false);
      }
    };
    fetchUnavailable();
  }, []);

  const handleAddUnavailable = async () => {
    if (!selectedUAUser || !uaDate) {
      alert("Please select a user and a date.");
      return;
    }
    setLoadingUnavailable(true);
    try {
      await fetch(`${API_BASE}/app/API/rbmgt/add_user_unavail_date.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: selectedUAUser, unavailable_date: uaDate }),
      });
      const res = await fetch(`${API_BASE}/app/API/rbmgt/get_unavailable_dates.php`);
      const updated = await res.json();
      setAllUnavailable(updated.unavail_dates || []);
      setUADate("");
    } catch (err) {
      console.error("Error adding unavailable date:", err);
    } finally {
      setLoadingUnavailable(false);
    }
  };

  const handleDeleteUnavailable = async (user_id, date) => {
    setLoadingUnavailable(true);
    try {
      await fetch(`${API_BASE}/app/API/rbmgt/delete_user_unavail_date.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id, unavailable_date: date }),
      });
      const res = await fetch(`${API_BASE}/app/API/rbmgt/get_unavailable_dates.php`);
      const updated = await res.json();
      setAllUnavailable(updated.unavail_dates || []);
    } catch (err) {
      console.error("Error deleting unavailable date:", err);
    } finally {
      setLoadingUnavailable(false);
    }
  };

  useEffect(() => {
    const fetchSpecials = async () => {
      setLoadingSpecial(true);
      try {
        const res = await fetch(`${API_BASE}/app/API/rbmgt/get_user_special_dates.php`);
        const data = await res.json();
        setAllSpecials(data.special_dates || []);
      } catch (err) {
        console.error("Failed to fetch special availabilities:", err);
      } finally {
        setLoadingSpecial(false);
      }
    };
    fetchSpecials();
  }, []);

  const handleAddSpecial = async () => {
    if (!selectedSAUser || !saDate) {
      alert("Please select a user and a date.");
      return;
    }
    setLoadingSpecial(true);
    try {
      await fetch(`${API_BASE}/app/API/rbmgt/add_user_special_date.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: selectedSAUser, special_date: saDate }),
      });
      const res = await fetch(`${API_BASE}/app/API/rbmgt/get_user_special_dates.php`);
      const newData = await res.json();
      setAllSpecials(newData.special_dates || []);
      setSADate("");
    } catch (err) {
      console.error("Error adding special availability:", err);
    } finally {
      setLoadingSpecial(false);
    }
  };

  const handleDeleteSpecial = async (user_id, date) => {
    setLoadingSpecial(true);
    try {
      await fetch(`${API_BASE}/app/API/rbmgt/delete_user_special_date.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id, special_date: date }),
      });
      const res = await fetch(`${API_BASE}/app/API/rbmgt/get_user_special_dates.php`);
      const updated = await res.json();
      setAllSpecials(updated.special_dates || []);
    } catch (err) {
      console.error("Error deleting special availability:", err);
    } finally {
      setLoadingSpecial(false);
    }
  };

  return (
    <div className="ua-container">
      <WeeklyAvailability
        allUsers={allUsers}
        weeklySettings={weeklySettings}
        setWeeklySettings={setWeeklySettings}
        handleSaveWeekly={handleSaveWeekly}
        loadingWeekly={loadingWeekly}
        handleStarClick={handleStarClick}
      />
      <UnavailableDates
        allUsers={allUsers}
        selectedUAUser={selectedUAUser}
        setSelectedUAUser={setSelectedUAUser}
        uaDate={uaDate}
        setUADate={setUADate}
        allUnavailable={allUnavailable}
        handleAddUnavailable={handleAddUnavailable}
        handleDeleteUnavailable={handleDeleteUnavailable}
        loadingUnavailable={loadingUnavailable}
      />
      <SpecialAvailabilities
        allUsers={allUsers}
        selectedSAUser={selectedSAUser}
        setSelectedSAUser={setSelectedSAUser}
        saDate={saDate}
        setSADate={setSADate}
        allSpecials={allSpecials}
        handleAddSpecial={handleAddSpecial}
        handleDeleteSpecial={handleDeleteSpecial}
        loadingSpecial={loadingSpecial}
      />
    </div>
  );
}
